/**
 * Share-URL safety net.
 *
 * Backend writes share URLs using the `PUBLIC_DOMAIN` env var. Preview
 * backend used to leak preview-domain URLs into shares (and recipients
 * would land on the dev environment with a "Wake up servers" banner).
 *
 * This helper enforces the contract: any share URL emitted from the
 * frontend ALWAYS points to `moodcard.app` — never to preview, never
 * to localhost, never to any other host. Even if a future config
 * regression re-introduces the bug, this function catches it.
 *
 * Pure string transform. Preserves path, query, hash. Safe to call on
 * already-correct URLs (idempotent).
 */
const CANONICAL_HOST = "moodcard.app";
const CANONICAL_ORIGIN = `https://${CANONICAL_HOST}`;

export function canonicalShareUrl(url) {
    if (!url || typeof url !== "string") return url;
    try {
        const u = new URL(url);
        if (u.host === CANONICAL_HOST) return url; // already canonical
        u.protocol = "https:";
        u.host = CANONICAL_HOST;
        u.port = "";
        return u.toString();
    } catch {
        // Not a parseable URL — leave it alone rather than corrupt it.
        return url;
    }
}

/**
 * Rewrite every URL inside a caption/message that points to a non-
 * canonical host. Catches "view in full → https://...preview.../open/x"
 * style auto-generated captions.
 */
export function rewriteCaptionUrls(text) {
    if (!text || typeof text !== "string") return text;
    return text.replace(/https?:\/\/[^\s)]+/g, (match) => {
        // Strip trailing punctuation that's commonly NOT part of the URL.
        const trailing = match.match(/[.,!?;)\]]+$/);
        const raw = trailing ? match.slice(0, -trailing[0].length) : match;
        const tail = trailing ? trailing[0] : "";
        return canonicalShareUrl(raw) + tail;
    });
}

export { CANONICAL_HOST, CANONICAL_ORIGIN };
